// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useEffect, useRef } from 'react';

/**
 * Custom hook that provides reliable auto-focus functionality for popover forms.
 * Uses a combination of requestAnimationFrame and retry mechanism to ensure
 * the input receives focus when the popover opens.
 */
export function usePopoverAutoFocus(isOpen: boolean) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus();
          return true; // Success
        }
        return false; // Failed
      };

      // Try multiple times with increasing delays if needed
      const tryFocus = (attempt = 1, maxAttempts = 5) => {
        if (focusInput()) {
          return; // Success, stop trying
        }

        if (attempt < maxAttempts) {
          const delay = attempt * 20; // 20ms, 40ms, 60ms, etc.
          setTimeout(() => {
            tryFocus(attempt + 1, maxAttempts);
          }, delay);
        }
      };

      // Primary strategy: Use requestAnimationFrame
      requestAnimationFrame(() => {
        if (!focusInput()) {
          // If RAF fails, start the retry mechanism
          tryFocus();
        }
      });

      // Fallback strategy: Try after a longer delay
      const fallbackTimer = setTimeout(() => {
        tryFocus();
      }, 100);

      return () => {
        clearTimeout(fallbackTimer);
      };
    }
  }, [isOpen]);

  return inputRef;
}
